import { useMsal } from '@azure/msal-react';
import {
    Button,
    Combobox,
    Dropdown,
    Input,
    Label,
    makeStyles,
    Option,
    SearchBox,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens,
    Tooltip,
} from '@fluentui/react-components';
import { FileTypeIcon } from '@fluentui/react-icons-file-type';
import React, { useEffect, useMemo, useState } from 'react';
import type { ProjektmitarbeiterItem } from '../../models/ProjektmitarbeiterItem';
import { DataService } from '../../services/dataService';
import { ReportService } from '../../services/reportService';
import { exportReportToExcel } from '../../utils/excelExport';
import { downloadFile, type ReportExportCell, type ReportExportRow, type ReportExportTable } from '../../utils/reportExport';
import { exportReportToWord } from '../../utils/wordExport';
import type { DateRange, ReportColumn, ReportDefinition } from './auswertungenReports';
import { REPORTS } from './auswertungenReports';

const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T/;
const MIN_SEARCH_LENGTH = 3;
const MIN_MITARBEITER_QUERY_LENGTH = 3;

const getMitarbeiterName = (mitarbeiter: ProjektmitarbeiterItem): string =>
    mitarbeiter.fullName || `${mitarbeiter.vorname} ${mitarbeiter.nachname}`;

const formatCell = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string' && ISO_DATE_TIME.test(value)) return new Date(value).toLocaleDateString();
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

// The API returns an <a> tag as a string. Only its href and text are rendered, and only http(s) links are allowed.
const renderLink = (value: string): React.ReactNode => {
    const anchor = new DOMParser().parseFromString(value, 'text/html').querySelector('a');
    const href = anchor?.getAttribute('href') ?? '';
    if (!/^https?:\/\//i.test(href)) return formatCell(value);
    return <a href={href} target="_blank" rel="noreferrer">{anchor?.textContent || 'Link'}</a>;
};

// Currency cells accept numbers and numeric strings, including German formats like "895 121,62".
const toAmount = (value: unknown): number | null => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string') return null;
    const compact = value.replace(/\s/g, '');
    const normalized = compact.includes(',') ? compact.replace(/\./g, '').replace(',', '.') : compact;
    if (normalized === '' || !Number.isFinite(Number(normalized))) return null;
    return Number(normalized);
};

// Formats one amount as EUR. Strings like "119033 / 341687,83" are split and each part is formatted, joined by " / ".
const formatCurrencyValue = (value: unknown): string | null => {
    const single = toAmount(value);
    if (single !== null) return single.toLocaleString('de-AT', { style: 'currency', currency: 'EUR' });
    if (typeof value !== 'string' || !value.includes('/')) return null;
    const parts = value.split('/').map(toAmount);
    if (parts.some((part) => part === null)) return null;
    return parts
        .map((part) => (part as number).toLocaleString('de-AT', { style: 'currency', currency: 'EUR' }))
        .join(' / ');
};

// Text used for searching and sorting; matches what the cell displays.
const cellText = (value: unknown, column: ReportColumn): string => {
    if (column.format === 'html' && typeof value === 'string') {
        return new DOMParser().parseFromString(value, 'text/html').body.textContent ?? '';
    }
    if (column.format === 'currency') {
        const currency = formatCurrencyValue(value);
        if (currency !== null) return currency;
    }
    if (column.format === 'number' && typeof value === 'number') {
        return value.toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return formatCell(value);
};

// Nulls always sort last, regardless of direction.
const compareCells = (a: unknown, b: unknown, direction: 'asc' | 'desc'): number => {
    if (a == null || b == null) {
        if (a == null && b == null) return 0;
        return a == null ? 1 : -1;
    }
    const result = typeof a === 'number' && typeof b === 'number'
        ? a - b
        : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    return direction === 'asc' ? result : -result;
};

const renderCell = (value: unknown, column: ReportColumn): React.ReactNode => {
    if (column.format === 'link' && typeof value === 'string' && value) {
        return renderLink(value);
    }
    // Server-provided markup, as in the original grid (HtmlEncode="false").
    if (column.format === 'html' && typeof value === 'string' && value) {
        return <span dangerouslySetInnerHTML={{ __html: value }} />;
    }
    if (column.format === 'currency') {
        const currency = formatCurrencyValue(value);
        if (currency !== null) return currency;
    }
    if (column.format === 'number' && typeof value === 'number') {
        return value.toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return formatCell(value);
};

// Export cells use the same text as the screen. Amounts also keep their numeric value for spreadsheets.
const toExportCell = (value: unknown, column: ReportColumn): ReportExportCell => {
    const text = cellText(value, column);
    const alignRight = column.align === 'end';
    if (column.format === 'currency') {
        const amount = toAmount(value);
        if (amount !== null) return { text, amount, currency: true, alignRight };
    }
    if (column.format === 'number' && typeof value === 'number') {
        return { text, amount: value, alignRight };
    }
    return { text, alignRight };
};

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: tokens.spacingHorizontalL,
        gap: tokens.spacingVerticalL,
    },
    tabsContainer: {
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
        marginBottom: tokens.spacingVerticalL,
    },
    content: {
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        justifyContent: 'center',
        paddingTop: tokens.spacingVerticalL,
    },
    reportPanel: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        width: '100%',
    },
    controls: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: tokens.spacingHorizontalM,
    },
    controlField: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        minWidth: '260px',
    },
    results: {
        marginTop: tokens.spacingVerticalL,
        width: '100%',
        overflow: 'auto',
    },
    groupRow: {
        fontWeight: tokens.fontWeightSemibold,
        backgroundColor: tokens.colorNeutralBackground3,
        paddingTop: '2px',
        paddingBottom: '2px',
    },
    compactCell: {
        paddingTop: '2px',
        paddingBottom: '2px',
        fontSize: tokens.fontSizeBase200,
    },
    reportTitle: {
        display: 'block',
        marginBottom: tokens.spacingVerticalS,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase400,
    },
    searchBox: {
        minWidth: '300px',
        maxWidth: '500px',
        marginBottom: tokens.spacingVerticalM,
    },
    searchRow: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
    },
    placeholder: {
        textAlign: 'center',
        color: tokens.colorNeutralForeground3,
        fontSize: '16px',
        fontWeight: 500,
    },
});

const Auswertungen: React.FC = () => {
    const styles = useStyles();
    const isDev = true;
    const { instance, accounts } = useMsal();

    const reportService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new ReportService(instance, accountInfo) : null;
    }, [instance, accounts]);

    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    const currentYear = new Date().getFullYear();
    const [selectedKey, setSelectedKey] = useState<string | undefined>();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [activeReport, setActiveReport] = useState<ReportDefinition | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [isXmlExporting, setIsXmlExporting] = useState(false);
    const [activeMeldeDatumYear, setActiveMeldeDatumYear] = useState<number | null>(null);
    const [rows, setRows] = useState<Record<string, unknown>[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
    const [mitarbeiterList, setMitarbeiterList] = useState<ProjektmitarbeiterItem[]>([]);
    const [mitarbeiterQuery, setMitarbeiterQuery] = useState('');
    const [selectedMitarbeiterId, setSelectedMitarbeiterId] = useState<number | null>(null);
    const [meldeDatumYear, setMeldeDatumYear] = useState<number>(currentYear);

    const selectedReport = REPORTS.find((r) => r.key === selectedKey);
    const parsedRange: DateRange | null =
        fromDate && toDate
            ? { from: new Date(`${fromDate}T00:00:00`), to: new Date(`${toDate}T23:59:59`) }
            : null;
    const datesValid = !!parsedRange && parsedRange.from.getTime() <= parsedRange.to.getTime();
    const resolvedMitarbeiterId = useMemo(() => {
        const text = mitarbeiterQuery.trim();
        const matches = mitarbeiterList.filter((m) => getMitarbeiterName(m) === text);
        if (matches.length === 0) return null;
        return (matches.find((m) => m.personalID === selectedMitarbeiterId) ?? matches[0]).personalID;
    }, [mitarbeiterList, mitarbeiterQuery, selectedMitarbeiterId]);

    const canView =
        !!selectedReport &&
        !!reportService &&
        !isLoading &&
        (!selectedReport.requiresDates || datesValid) &&
        (!selectedReport.requiresMitarbeiter || resolvedMitarbeiterId !== null);

    useEffect(() => {
        if (!selectedReport?.requiresMitarbeiter || !dataService || mitarbeiterList.length > 0) return;
        dataService
            .getMitarbeiter()
            .then(setMitarbeiterList)
            .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    }, [selectedReport?.requiresMitarbeiter, dataService, mitarbeiterList.length]);

    const filteredMitarbeiter = useMemo(() => {
        const term = mitarbeiterQuery.trim().toLowerCase();
        if (term.length < MIN_MITARBEITER_QUERY_LENGTH) return mitarbeiterList;
        return mitarbeiterList.filter((m) => getMitarbeiterName(m).toLowerCase().includes(term));
    }, [mitarbeiterList, mitarbeiterQuery]);

    const columns = useMemo(
        () => Array.from(new Set(rows.flatMap((row) => Object.keys(row)))),
        [rows],
    );

    const handleView = async () => {
        if (!selectedReport || !reportService) return;
        if (selectedReport.requiresMitarbeiter && resolvedMitarbeiterId === null) return;

        setIsLoading(true);
        setError(null);
        try {
            // Non-date reports ignore the range, so a placeholder is passed.
            const range = selectedReport.requiresDates && parsedRange
                ? parsedRange
                : { from: new Date(), to: new Date() };
            const meldeDatum = selectedReport.requiresMeldeDatum
                ? new Date(meldeDatumYear, 3, 15)
                : null;
            const data = selectedReport.fetch
                ? await selectedReport.fetch(reportService, range, resolvedMitarbeiterId, meldeDatum)
                : [];
            console.log('Fetched data:', data);
            setRows(data as Record<string, unknown>[]);
            setActiveReport(selectedReport);
            setActiveMeldeDatumYear(selectedReport.requiresMeldeDatum ? meldeDatumYear : null);
            setSearchInput('');
            setAppliedSearch('');
            setSortField(null);
            setSortDirection('asc');
            setCollapsedGroups(new Set());
        } catch (err) {
            setRows([]);
            setActiveReport(null);
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        const term = value.trim().toLowerCase();
        setAppliedSearch(term.length >= MIN_SEARCH_LENGTH ? term : '');
    };

    const toggleGroup = (group: string) => {
        setCollapsedGroups((current) => {
            const next = new Set(current);
            if (next.has(group)) {
                next.delete(group);
            } else {
                next.add(group);
            }
            return next;
        });
    };

    // Filtering, sorting and row building only run when the applied search or sort changes, not on every keystroke.
    const tableModel = useMemo(() => {
        if (!activeReport || rows.length === 0) return null;

        const today = new Date();
        const prepared = activeReport.prepare?.(rows, today);
        const view = prepared ? prepared.view : activeReport.view?.(today);
        const sourceRows = prepared ? prepared.rows : rows;
        const tableColumns: ReportColumn[] = view?.columns
            ?? columns.map((column) => ({ field: column, header: column }));
        const groupBy = view?.groupBy;

        // Links are not searched or sorted, matching the AllowSorting="false" grids.
        const searchableColumns = tableColumns.filter((column) => column.format !== 'link');
        const filteredRows = appliedSearch
            ? sourceRows.filter((row) => searchableColumns.some((column) =>
                cellText(row[column.field], column).toLowerCase().includes(appliedSearch)))
            : sourceRows;
        // Groups stay contiguous; the sort column orders rows within each group.
        const sortedRows = [...filteredRows].sort((a, b) => {
            if (groupBy) {
                const groupOrder = compareCells(a[groupBy], b[groupBy], 'asc');
                if (groupOrder !== 0) return groupOrder;
            }
            return sortField ? compareCells(a[sortField], b[sortField], sortDirection) : 0;
        });

        const bodyRows: React.ReactNode[] = [];
        const exportRows: ReportExportRow[] = [];
        let previousGroup: string | undefined;
        sortedRows.forEach((row, rowIndex) => {
            if (groupBy) {
                const group = formatCell(row[groupBy]);
                if (group !== previousGroup) {
                    const isCollapsed = collapsedGroups.has(group);
                    bodyRows.push(
                        <TableRow key={`group-${rowIndex}`} onClick={() => toggleGroup(group)} style={{ cursor: 'pointer' }}>
                            <TableCell colSpan={tableColumns.length} className={styles.groupRow}>
                                {isCollapsed ? '▸' : '▾'} {group}
                            </TableCell>
                        </TableRow>,
                    );
                    exportRows.push({ group });
                    previousGroup = group;
                }
                if (collapsedGroups.has(group)) return;
            }
            exportRows.push({ cells: tableColumns.map((column) => toExportCell(row[column.field], column)) });
            bodyRows.push(
                <TableRow key={rowIndex}>
                    {tableColumns.map((column) => (
                        <TableCell key={column.field} className={styles.compactCell} style={{ textAlign: column.align }}>{renderCell(row[column.field], column)}</TableCell>
                    ))}
                </TableRow>,
            );
        });
        if (sortedRows.length === 0) {
            bodyRows.push(
                <TableRow key="no-match">
                    <TableCell colSpan={tableColumns.length} className={styles.placeholder}>No rows match the search.</TableCell>
                </TableRow>,
            );
        }

        return { tableColumns, bodyRows, exportRows };
    }, [activeReport, rows, columns, appliedSearch, sortField, sortDirection, collapsedGroups, styles]);

    const handleExport = async (format: 'xlsx' | 'docx') => {
        if (!activeReport || !tableModel) return;
        const table: ReportExportTable = {
            title: activeReport.label,
            headers: tableModel.tableColumns.map((column) => column.header),
            rows: tableModel.exportRows,
        };
        const baseName = activeReport.label.replace(/[\\/:*?"<>|]/g, '').trim() || 'Report';
        const fileName = activeReport.key === 'bisMeldung' && activeMeldeDatumYear !== null
            ? `${baseName}_15.04.${activeMeldeDatumYear}`
            : baseName;
        setIsExporting(true);
        try {
            if (format === 'xlsx') {
                await exportReportToExcel(table, fileName);
            } else {
                await exportReportToWord(table, fileName);
            }
        } catch (error) {
            console.error(`Error exporting report to ${format}:`, error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleBisXmlExport = async () => {
        if (activeMeldeDatumYear === null || !reportService) return;
        // Sent in the legacy de-DE short date format (dd.MM.yyyy).
        const meldeDatumString = `15.04.${activeMeldeDatumYear}`;
        // Filename mirrors the legacy app: Erh006_{0:d}_FuE.xml with the de-DE short date (dd.MM.yyyy).
        const fileName = `Erh006_${meldeDatumString}_FuE.xml`;
        setIsXmlExporting(true);
        try {
            const xml = await reportService.getBisMeldungString(new Date(activeMeldeDatumYear, 3, 15));
            downloadFile(new Blob([xml], { type: 'application/xml' }), fileName);
        } catch (error) {
            console.error('Error exporting BIS-Meldung XML:', error);
        } finally {
            setIsXmlExporting(false);
        }
    };

    const renderResults = () => {
        if (isLoading) {
            return <Spinner label="Loading report..." />;
        }
        if (error) {
            return <p className={styles.placeholder}>{error}</p>;
        }
        if (!activeReport) {
            return <p className={styles.placeholder}>Select a report and press View.</p>;
        }
        if (!tableModel) {
            return <p className={styles.placeholder}>No data available for this report.</p>;
        }
        const { tableColumns, bodyRows, exportRows } = tableModel;

        return (
            <>
                <span className={styles.reportTitle}>{activeReport.label}</span>
                <div className={styles.searchRow}>
                    <SearchBox
                        className={styles.searchBox}
                        placeholder="Search in report..."
                        value={searchInput}
                        onChange={(_, data) => handleSearchChange(data.value)}
                    />
                    <Tooltip content="Export visible rows to Excel" relationship="label">
                        <Button
                            appearance="subtle"
                            icon={<FileTypeIcon extension="xlsx" size={24} />}
                            disabled={exportRows.length === 0 || isExporting}
                            onClick={() => handleExport('xlsx')}
                        />
                    </Tooltip>
                    <Tooltip content="Export visible rows to Word" relationship="label">
                        <Button
                            appearance="subtle"
                            icon={<FileTypeIcon extension="docx" size={24} />}
                            disabled={exportRows.length === 0 || isExporting}
                            onClick={() => handleExport('docx')}
                        />
                    </Tooltip>
                    {activeReport.key === 'bisMeldung' && (
                        <Tooltip content="Download BIS-Meldung XML (Erh006)" relationship="label">
                            <Button
                                appearance="subtle"
                                icon={isXmlExporting
                                    ? <Spinner size="tiny" />
                                    : <FileTypeIcon extension="xml" size={24} />}
                                disabled={isXmlExporting || activeMeldeDatumYear === null}
                                onClick={handleBisXmlExport}
                            />
                        </Tooltip>
                    )}
                </div>
                <Table size="small" aria-label={activeReport.label}>
                    <TableHeader>
                        <TableRow style={{ backgroundColor: tokens.colorNeutralBackground2, borderBottom: `2px solid ${tokens.colorNeutralStroke1}` }}>
                            {tableColumns.map((column) => {
                                const sortable = column.format !== 'link';
                                return (
                                    <TableHeaderCell
                                        key={column.field}
                                        className={styles.compactCell}
                                        style={{ fontWeight: 700, width: column.width, textAlign: column.align, cursor: sortable ? 'pointer' : undefined }}
                                        onClick={sortable ? () => handleSort(column.field) : undefined}
                                    >
                                        {column.header}
                                        {sortField === column.field && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                                    </TableHeaderCell>
                                );
                            })}
                        </TableRow>
                    </TableHeader>
                    <TableBody>{bodyRows}</TableBody>
                </Table>
            </>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.tabsContainer}>
            </div>
            {isDev && <div className={styles.content}>
                <p className={styles.placeholder}>Evaluations content will be displayed here.</p>
            </div>}
            {!isDev && <div className={styles.content}>
                <div className={styles.reportPanel}>
                    <div className={styles.controls}>
                        <div className={styles.controlField}>
                            <Label htmlFor="report-dropdown">Report</Label>
                            <Dropdown
                                id="report-dropdown"
                                placeholder="Select a report..."
                                disabled={isLoading}
                                value={selectedReport?.label ?? ''}
                                selectedOptions={selectedKey ? [selectedKey] : []}
                                onOptionSelect={(_, data) => setSelectedKey(data.optionValue)}
                            >
                                {REPORTS.map((report) => (
                                    <Option key={report.key} value={report.key} text={report.label}>
                                        {report.label}
                                    </Option>
                                ))}
                            </Dropdown>
                        </div>
                        {selectedReport?.requiresMitarbeiter && (
                            <div className={styles.controlField}>
                                <Label htmlFor="report-mitarbeiter">Mitarbeiter</Label>
                                <Combobox
                                    id="report-mitarbeiter"
                                    open={mitarbeiterQuery.trim().length >= MIN_MITARBEITER_QUERY_LENGTH}
                                    placeholder="Mind. 3 Zeichen eingeben..."
                                    disabled={isLoading}
                                    value={mitarbeiterQuery}
                                    selectedOptions={resolvedMitarbeiterId !== null ? [String(resolvedMitarbeiterId)] : []}
                                    onChange={(event) => setMitarbeiterQuery(event.currentTarget.value)}
                                    onOptionSelect={(_, data) => {
                                        const id = Number(data.optionValue);
                                        const mitarbeiter = mitarbeiterList.find((m) => m.personalID === id);
                                        setSelectedMitarbeiterId(id);
                                        setMitarbeiterQuery(mitarbeiter ? getMitarbeiterName(mitarbeiter) : '');
                                    }}
                                >
                                    {mitarbeiterQuery.trim().length < MIN_MITARBEITER_QUERY_LENGTH ? null : filteredMitarbeiter.length > 0 ? (
                                        filteredMitarbeiter.map((mitarbeiter) => {
                                            const displayName = getMitarbeiterName(mitarbeiter);
                                            return (
                                                <Option key={mitarbeiter.personalID} value={mitarbeiter.personalID.toString()} text={displayName}>
                                                    {displayName}
                                                </Option>
                                            );
                                        })
                                    ) : (
                                        <Option value="" disabled>
                                            Keine Mitarbeiter gefunden
                                        </Option>
                                    )}
                                </Combobox>
                            </div>
                        )}
                        {selectedReport?.requiresMeldeDatum && (
                            <div className={styles.controlField}>
                                <Label htmlFor="report-meldedatum">Meldedatum</Label>
                                <Dropdown
                                    id="report-meldedatum"
                                    disabled={isLoading}
                                    value={`15.04.${meldeDatumYear}`}
                                    selectedOptions={[String(meldeDatumYear)]}
                                    onOptionSelect={(_, data) => setMeldeDatumYear(Number(data.optionValue))}
                                >
                                    {[currentYear + 1, currentYear, currentYear - 1].map((year) => (
                                        <Option key={year} value={String(year)} text={`15.04.${year}`}>
                                            {`15.04.${year}`}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                        )}
                        {selectedReport?.requiresDates && (
                            <>
                                <div className={styles.controlField}>
                                    <Label htmlFor="report-from-date">From</Label>
                                    <Input
                                        id="report-from-date"
                                        type="date"
                                        value={fromDate}
                                        disabled={isLoading}
                                        onChange={(_, data) => setFromDate(data.value)}
                                    />
                                </div>
                                <div className={styles.controlField}>
                                    <Label htmlFor="report-to-date">To</Label>
                                    <Input
                                        id="report-to-date"
                                        type="date"
                                        value={toDate}
                                        disabled={isLoading}
                                        onChange={(_, data) => setToDate(data.value)}
                                    />
                                </div>
                            </>
                        )}
                        <Button appearance="primary" disabled={!canView} onClick={handleView}>
                            View
                        </Button>
                    </div>
                    <div className={styles.results}>
                        {renderResults()}
                    </div>
                </div>
            </div>}
        </div>
    );
}

export default Auswertungen;
